'use client';

import { XMarkIcon } from '@heroicons/react/16/solid';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { BookmarkPlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Input } from '../../ui/input';
import { useDebounceCallback } from 'usehooks-ts';
import { Card } from '../../ui/card';
import { presets } from './presets';
import { useWorkflows } from '@/providers/workflow-provider';
import { toast } from 'sonner';
import {
  createEmptyUserBookmarks,
  createEmptyWorkEngagementBookmarks,
} from '@/lib/bookmark/utils/bookmark-utils';
import type {
  UserBookmarks,
  WorkEngagementBookmarks,
} from '@/lib/bookmark/types';
import { LoaderButton } from '@/components/ui/loader-button';
import { getMaxRootFolderOrder, workflowPresetToFolder } from './utils';
import { cn } from '@/lib/cn';

interface WorkflowPresetsModalProps {
  readonly onOpenChange: (open: boolean) => void;
}

export function WorkflowPresetsModal({
  onOpenChange,
}: WorkflowPresetsModalProps) {
  const { save, workflows, isSaving, isWorkEngagementBookmarksContext } =
    useWorkflows();
  const [selectedPresetIds, setSelectedPresetIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const debounced = useDebounceCallback(setSearch, 500);

  const filteredPresets = useMemo(() => {
    const q = search.toLowerCase();
    return presets.filter(
      (preset) =>
        preset.name.toLowerCase().includes(q) ||
        preset.description.toLowerCase().includes(q)
    );
  }, [search]);

  const presetById = useMemo(() => {
    return new Map(presets.map((p) => [p.presetId, p]));
  }, []);

  const handleSave = useCallback((): void => {
    if (selectedPresetIds.length === 0) {
      toast.info('Välj minst ett arbetsflöde att lägga till');
      return;
    }

    const emptyBase = isWorkEngagementBookmarksContext
      ? createEmptyWorkEngagementBookmarks()
      : createEmptyUserBookmarks();

    const existingFolders = workflows?.folders ?? emptyBase.folders;
    let nextOrder = getMaxRootFolderOrder(existingFolders) + 1;
    const foldersToAdd = selectedPresetIds.flatMap((id) => {
      const preset = presetById.get(id);
      if (!preset) {
        return [];
      }
      const folder = workflowPresetToFolder(preset);
      const withOrder = { ...folder, order: nextOrder };
      nextOrder += 1;
      return [withOrder];
    });

    const merged = {
      ...(workflows ?? emptyBase),
      folders: [...existingFolders, ...foldersToAdd],
    };

    const savePromise = save(merged as UserBookmarks | WorkEngagementBookmarks);

    toast.promise(savePromise, {
      loading: 'Sparar arbetsflöden...',
      success: () => {
        setSelectedPresetIds([]);
        onOpenChange(false);
        return 'Arbetsflöden har lagts till';
      },
      error: 'Kunde inte spara arbetsflöden',
    });
  }, [
    isWorkEngagementBookmarksContext,
    onOpenChange,
    presetById,
    save,
    selectedPresetIds,
    workflows,
  ]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className='p-0' size='md'>
        <DialogHeader className='flex-col items-start gap-1'>
          <div className='flex w-full flex-row items-center justify-between'>
            <DialogTitle>Färdiga arbetsflöden från Pigello</DialogTitle>
            <Button
              className='items-end'
              variant='outline'
              size='icon-sm'
              onClick={() => onOpenChange(false)}
            >
              <XMarkIcon className='size-4' />
            </Button>
          </div>
          <DialogDescription>
            Du kan också skapa egna arbetsflöden genom att spara dina favoriter
            genom att klicka på{' '}
            <BookmarkPlusIcon className='inline-block size-4' /> uppe till höger
            på sidan du vill spara.
          </DialogDescription>
        </DialogHeader>
        <div className='flex h-[500px] flex-col'>
          <div className='p-4'>
            <Input
              className='w-80'
              placeholder={'Sök bland arbetsflöden...'}
              defaultValue=''
              onChange={(event) => {
                debounced(event.target.value);
              }}
            />
          </div>
          <div className='flex h-full flex-col gap-2 overflow-y-auto border-t p-4'>
            {filteredPresets.map((preset) => (
              <Card
                className={cn(
                  'flex cursor-pointer flex-row items-center justify-between border-b p-4 shadow-none transition-colors hover:bg-accent/50',
                  selectedPresetIds.includes(preset.presetId) &&
                    'border-primary bg-primary/5'
                )}
                key={preset.presetId}
                onClick={() =>
                  setSelectedPresetIds(
                    selectedPresetIds.includes(preset.presetId)
                      ? selectedPresetIds.filter((id) => id !== preset.presetId)
                      : [...selectedPresetIds, preset.presetId]
                  )
                }
              >
                <div className='flex flex-col gap-1'>
                  <div className='text-base font-medium'>{preset.name}</div>
                  <p className='text-xs text-muted-foreground'>
                    {preset.description}
                  </p>
                </div>
                <Button
                  variant={
                    selectedPresetIds.includes(preset.presetId)
                      ? 'default'
                      : 'outline'
                  }
                >
                  {selectedPresetIds.includes(preset.presetId)
                    ? 'Tillagd'
                    : 'Lägg till'}
                </Button>
              </Card>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={isSaving}
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            Avbryt
          </Button>
          <LoaderButton
            isLoading={isSaving}
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            Spara
          </LoaderButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
